import { useParams, useNavigate } from "@tanstack/react-router";

import { useForm } from "@tanstack/react-form";
import {
  materialFormSchema,
  type MaterialFormData,
} from "../../../shared/schemas/material.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/shared/FormattedNumberInput";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, cleanBrandName, validateUploadFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from "@/lib/utils";
import { focusFirstError } from "../../../lib/form/formUtils";
import { normalizeNumericFields } from "@/lib/form/sanitize";
import { ArrowLeft, Save, Loader2, AlertTriangle, Wand2, Upload, X, Sparkles } from "lucide-react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { trpcClient } from "@/lib/trpc";
import { uploadApi } from "@/lib/api-client";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { ComboboxConfig } from "@/components/shared/ComboboxConfig";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { parseMaterialSpecs } from "./utils/parseMaterialSpecs";
import {
  useVendorLookup,
  useMaterialTypeLookup,
  useUnitLookup,
} from "@/hooks/useFKLookup";
import { EditableCombobox } from "@/components/shared/EditableCombobox";
import ImageLightbox from "@/components/shared/ImageLightbox";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { toast } from "sonner";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { useRoleAccess } from "@/hooks/useRoleAccess";

function strNormalize(str: string) {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d");
}

async function compressImage(
  dataUrl: string,
  maxSizeKB = 2000,
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const sizeKB = (dataUrl.length * 3) / 4 / 1024;
      if (sizeKB <= maxSizeKB) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      const scale = Math.sqrt(maxSizeKB / sizeKB);
      width = Math.round(width * Math.min(scale, 1));
      height = Math.round(height * Math.min(scale, 1));
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, width, height);
      let quality = 0.85;
      let result = canvas.toDataURL("image/jpeg", quality);
      while ((result.length * 3) / 4 / 1024 > maxSizeKB && quality > 0.3) {
        quality -= 0.1;
        result = canvas.toDataURL("image/jpeg", quality);
      }
      resolve(result);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export interface MaterialFormProps {
  materialId?: string;
  defaultValues?: Partial<MaterialFormData>;
  onSubmit?: (payload: any) => Promise<void | any>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  isModal?: boolean;
  lockedFields?: string[];
  breadcrumb?: string[];
}

const MATERIAL_NUMERIC_FIELDS = [
  "width",
  "length",
  "height",
  "unitPrice",
  "initialStock",
  "currentStock",
  "minStock",
] as const;

export function MaterialForm(props?: MaterialFormProps) {
  const routeParams = (useParams({ strict: false }) as any) || {};
  const materialId = props?.materialId || routeParams.materialId;
  const isEdit = !!materialId && materialId !== "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isModal = props?.isModal ?? false;

  const { data: initialData, isLoading } = useQuery({
    queryKey: ["material", materialId],
    queryFn: () => trpcClient.materials.getById.query({ id: materialId }),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => trpcClient.materials.create.mutate(data),
    onSuccess: (res) => {
      toast.success("Đã tạo vật tư");
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      navigate({ to: "/materials" });
    },
    onError: (err: any) => toast.error("Lỗi: " + extractErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => {
      const targetId = materialId || data.id || data.materialId;
      if (!targetId) {
        throw new Error("Không xác định được ID vật tư để cập nhật");
      }
      const { id: _id, materialId: _mId, ...updateData } = data;
      return trpcClient.materials.update.mutate({
        id: targetId,
        data: updateData,
      });
    },
    onSuccess: (res) => {
      toast.success("Đã cập nhật vật tư");
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      queryClient.invalidateQueries({ queryKey: ["material", materialId] });
      navigate({ to: "/materials" });
    },
    onError: (err: any) => toast.error("Lỗi: " + extractErrorMessage(err)),
  });

  const onSubmit = async (payload: any) => {
    if (props?.onSubmit) {
      await props.onSubmit(payload);
    } else if (isEdit) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const defaultValues = {
    ...(initialData || {}),
    ...(props?.defaultValues || {}),
  };


  const qc = useQueryClient();
  const vendorLookup = useVendorLookup();
  const materialTypeLookup = useMaterialTypeLookup();
  const unitLookup = useUnitLookup();
  const { hasPermission } = useRoleAccess();

  const { data: existingMaterials = [] } = useQuery({
    queryKey: ["materials-all-check"],
    queryFn: () =>
      trpcClient.materials.list.query({ limit: 50 }).then((res) => res.items),
  });

  const form = useForm({
    defaultValues: {
      materialId: defaultValues?.materialId ?? "",
      name: defaultValues?.name ?? "",
      category: defaultValues?.category ?? ("PAPER" as const),
      subType: defaultValues?.subType ?? "",
      typeId: defaultValues?.typeId ?? "",
      vendorId: defaultValues?.vendorId ?? "",
      specs: defaultValues?.specs ?? "",
      gsm: defaultValues?.gsm ?? "",
      brand: defaultValues?.brand ?? "",
      width: defaultValues?.width ?? undefined,
      length: defaultValues?.length ?? undefined,
      height: defaultValues?.height ?? undefined,
      unit: defaultValues?.unit ?? "",
      unitPrice: defaultValues?.unitPrice ?? undefined,
      initialStock: defaultValues?.initialStock ?? undefined,
      currentStock: defaultValues?.currentStock ?? undefined,
      minStock: defaultValues?.minStock ?? undefined,
      note: defaultValues?.note ?? "",
      image: defaultValues?.image ?? "",
      status: defaultValues?.status ?? ("ACTIVE" as const),
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: materialFormSchema as any },
    onSubmit: async ({ value }) => {
      // Ép các trường số (Decimal từ DB trả về dạng chuỗi) về number trước khi gửi API
      const payload = normalizeNumericFields({ ...value } as any, MATERIAL_NUMERIC_FIELDS);
      if (!payload.materialId) delete payload.materialId;
      return onSubmit(payload);
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  const [imagePreview, setImagePreview] = useState<string>(
    defaultValues?.image ?? "",
  );
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setCompressedImage = useCallback(
    async (dataUrl: string) => {
      setIsUploading(true);
      try {
        const compressed = await compressImage(dataUrl, 1000);
        setImagePreview(compressed);

        const arr = compressed.split(",");
        const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const file = new File([u8arr], "material-image.jpg", { type: mime });

        const { fileUrl } = await uploadApi.uploadFile(file);

        form.setFieldValue("image", fileUrl);
        setImagePreview(fileUrl);
      } catch (error) {
        toast.error("Lỗi tải ảnh lên server: " + extractErrorMessage(error));
      } finally {
        setIsUploading(false);
      }
    },
    [form],
  );

  const handleImagePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const validation = validateUploadFile(file, MAX_MEDIA_IMAGE_SIZE, "Ảnh vật tư");
            if (!validation.valid) {
              toast.error(validation.error);
              return;
            }
            const reader = new FileReader();
            reader.onload = (ev) =>
              setCompressedImage(ev.target?.result as string);
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    },
    [setCompressedImage],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file, MAX_MEDIA_IMAGE_SIZE, "Ảnh vật tư");
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCompressedImage(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Calculate unique groups for "Nhóm vật tư" Autocomplete
  const groupOptions = useMemo(() => {
    const groups = materialTypeLookup.options
      .map((opt) => opt.original?.group)
      .filter(Boolean);
    return Array.from(new Set(groups)) as string[];
  }, [materialTypeLookup.options]);

  const brandOptions = useMemo(() => {
    const brands = existingMaterials.map((m: any) => m.brand).filter(Boolean);
    return Array.from(new Set(brands)).map(String);
  }, [existingMaterials]);

  const gsmOptions = useMemo(() => {
    const gsms = existingMaterials.map((m: any) => m.gsm).filter(Boolean);
    return Array.from(new Set(gsms)).map(String);
  }, [existingMaterials]);

  // Filter "Loại vật tư" by "Nhóm vật tư"
  const currentGroup = form.state.values.subType; // We map "Nhóm vật tư" to subType field
  const filteredMaterialTypes = useMemo(() => {
    if (!currentGroup) return materialTypeLookup.options;
    const cg = String(currentGroup).trim().toLowerCase();
    return materialTypeLookup.options.filter((opt) => {
      const grp = String(opt.original?.group || "")
        .trim()
        .toLowerCase();
      return grp === cg;
    });
  }, [materialTypeLookup.options, currentGroup]);

  const handleGenerateInfo = useCallback(
    (overrides?: Partial<MaterialFormData>) => {
      const s = { ...form.state.values, ...overrides } as any;
      if (!s.subType || !s.typeId) {
        return;
      }

      const typeOption = materialTypeLookup.options.find(
        (opt) => opt.id === s.typeId,
      );
      const typeLabel = typeOption ? typeOption.label : "";

      // Kích thước (DxRxC)
      const sizeParts = [];
      if (s.length) sizeParts.push(s.length);
      if (s.width) sizeParts.push(s.width);
      if (s.height) sizeParts.push(s.height);
      const sizeStr = sizeParts.length > 0 ? ` ${sizeParts.join("x")}mm` : "";

      const gsmStr = s.gsm ? ` ${s.gsm}gsm` : "";
      const cleanBrand = cleanBrandName(s.brand);
      const brandStr = cleanBrand ? ` ${cleanBrand}` : "";

      let vendorStr = "";
      if (s.vendorId) {
        const vendorOpt = vendorLookup.options.find(
          (v: any) => v.id === s.vendorId,
        );
        if (vendorOpt) {
          const code =
            vendorOpt.original?.code || vendorOpt.original?.shortName;
          if (code) {
            vendorStr = ` (${code})`;
          } else {
            const firstWord = vendorOpt.label.split(" ")[0] || "";
            if (firstWord && !firstWord.includes("["))
              vendorStr = ` (${firstWord})`;
          }
        }
      }

      const cleanSubType = String(s.subType || "")
        .replace(/^(material_subtypes_)/i, "")
        .trim();
      const newName =
        `${cleanSubType} ${typeLabel}${gsmStr}${brandStr}${sizeStr}${vendorStr}`
          .trim()
          .replace(/\s+/g, " ");

      // Only auto-update name if not manually changed or if it's empty
      const currentName = form.getFieldValue("name") as string;
      if (
        !currentName ||
        currentName.startsWith(s.subType) ||
        currentName.startsWith(cleanSubType) ||
        !defaultValues?.name
      ) {
        form.setFieldValue("name", newName);
      }
    },
    [
      form,
      materialTypeLookup.options,
      vendorLookup.options,
      existingMaterials,
      defaultValues,
    ],
  );

  const handleSmartParse = useCallback((targetName?: string) => {
    const raw = targetName !== undefined ? targetName : (form.getFieldValue("name") as string) || defaultValues?.name || "";
    if (!raw.trim()) {
      toast.warning("Vui lòng nhập tên hoặc quy cách vật tư để bóc tách");
      return;
    }

    const parsed = parseMaterialSpecs(raw, materialTypeLookup.options as any);
    let updatedCount = 0;

    if (parsed.subType) {
      form.setFieldValue("subType", parsed.subType);
      updatedCount++;
    }
    if (parsed.typeId) {
      form.setFieldValue("typeId", parsed.typeId);
      updatedCount++;
    }
    if (parsed.gsm) {
      form.setFieldValue("gsm", String(parsed.gsm));
      updatedCount++;
    }
    if (parsed.length !== undefined) {
      form.setFieldValue("length", parsed.length);
      updatedCount++;
    }
    if (parsed.width !== undefined) {
      form.setFieldValue("width", parsed.width);
      updatedCount++;
    }
    if (parsed.height !== undefined) {
      form.setFieldValue("height", parsed.height);
      updatedCount++;
    }
    if (parsed.specs) {
      form.setFieldValue("specs", parsed.specs);
      updatedCount++;
    }

    if (updatedCount > 0) {
      toast.success(`Đã tự động bóc tách ${updatedCount} thông số từ quy cách!`);
    } else {
      toast.warning("Không tìm thấy quy cách mới trong tên");
    }
  }, [form, defaultValues?.name, materialTypeLookup.options]);

  // Auto-parse on initial load if name is provided and fields are blank
  useEffect(() => {
    if (defaultValues?.name && !defaultValues?.gsm && !defaultValues?.length && !defaultValues?.width) {
      const parsed = parseMaterialSpecs(defaultValues.name, materialTypeLookup.options as any);
      if (parsed.subType) form.setFieldValue("subType", parsed.subType);
      if (parsed.typeId) form.setFieldValue("typeId", parsed.typeId);
      if (parsed.gsm) form.setFieldValue("gsm", String(parsed.gsm));
      if (parsed.length) form.setFieldValue("length", parsed.length);
      if (parsed.width) form.setFieldValue("width", parsed.width);
      if (parsed.specs) form.setFieldValue("specs", parsed.specs);
    }
  }, [defaultValues?.name, materialTypeLookup.options]);

  const duplicateWarnings = useMemo(() => {
    const currentName = strNormalize(form.state.values.name || "");
    if (currentName.length < 5) return [];
    return existingMaterials
      .filter((m: any) => {
        if (
          defaultValues?.materialId &&
          m.materialId === defaultValues.materialId
        )
          return false;
        return m.name && strNormalize(m.name) === currentName;
      })
      .slice(0, 3);
  }, [form.state.values.name, existingMaterials, defaultValues?.materialId]);

  // Compact field
  const F = ({
    name,
    label,
    required,
    placeholder,
    type = "text",
    span = 1,
    onChangeHook,
  }: {
    name: keyof Omit<MaterialFormData, "category">;
    label: string;
    required?: boolean;
    placeholder?: string;
    type?: string;
    span?: 1 | 2 | 3 | 4;
    onChangeHook?: (val: string, oldVal: string) => void;
  }) => (
    <form.Field name={name}>
      {(field: any) => (
        <div
          className={cn(
            span === 2 && "col-span-2 md:col-span-2",
            span === 3 && "col-span-2 md:col-span-3",
            span === 4 && "col-span-2 md:col-span-4",
          )}
        >
          <TanstackFieldWrapper field={field} label={label} required={required}>
            <TanstackFormControl>
              {type === "number" ? (
                <FormattedNumberInput
                  id={field.name}
                  value={field.state.value as any}
                  placeholder={placeholder}
                  className="h-9"
                  onValueChange={(val) => {
                    const oldVal = (field.state.value as string) ?? "";
                    field.handleChange(val as any);
                    onChangeHook?.(
                      val !== undefined && val !== null ? String(val) : "",
                      oldVal,
                    );
                  }}
                  onBlur={field.handleBlur}
                />
              ) : (
                <Input
                  id={field.name}
                  type={type}
                  value={(field.state.value as string) ?? ""}
                  placeholder={placeholder}
                  className="h-9"
                  onChange={(e) => {
                    const oldVal = (field.state.value as string) ?? "";
                    field.handleChange(e.target.value as any);
                    onChangeHook?.(e.target.value, oldVal);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            </TanstackFormControl>
          </TanstackFieldWrapper>
        </div>
      )}
    </form.Field>
  );

  const SH = ({
    label,
    color = "text-indigo-700",
  }: {
    label: string;
    color?: string;
  }) => (
    <h3
      className={cn("text-sm font-bold uppercase tracking-wider pt-2", color)}
    >
      {label}
    </h3>
  );

  
  if (isEdit && isLoading) return <div className="p-8 text-center text-slate-500">Đang tải dữ liệu...</div>;

    const actions = (
    <div className="flex items-center space-x-2">
      <Button 
        type="button" 
        variant="outline" 
        size="sm"
        onClick={() => props?.onCancel ? props.onCancel() : navigate({ to: "/materials" })}
        disabled={props?.isSubmitting || createMutation.isPending || updateMutation.isPending}
      >
        Hủy
      </Button>
      <Button 
        type="button" 
        size="sm" 
        className="bg-indigo-600 hover:bg-indigo-700 text-white"
        disabled={props?.isSubmitting || createMutation.isPending || updateMutation.isPending} 
        onClick={handleFormSubmit}
      >
        <Save className="w-4 h-4 mr-1.5" />
        {props?.submitLabel || (isEdit ? "Cập nhật" : "Tạo mới")}
      </Button>
    </div>
  );

  const formContent = (
    <form onSubmit={handleFormSubmit} className="space-y-3">

      {duplicateWarnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">
              Cảnh báo trùng lặp tên vật tư:
            </p>
            <ul className="mt-1 space-y-0.5 text-amber-700">
              {duplicateWarnings.map((m: any) => (
                <li key={m.id}>
                  • <strong>{m.materialId}</strong> — {m.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="border rounded-lg bg-white shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <SH label="Phân loại & Định danh" />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="btn-smart-parse-specs"
              className="h-8 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200"
              onClick={() => handleSmartParse()}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1 text-amber-600" /> Bóc tách quy cách
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 text-xs font-medium"
              onClick={() => handleGenerateInfo()}
            >
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Tạo Tên & Mã
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Hàng 1: Nhóm vật tư, Loại vật tư, Định lượng, Nhãn hiệu */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <form.Field name="subType">
              {(field: any) => (
                <div className="space-y-1 col-span-2 md:col-span-1">
                  <ComboboxConfigMaster
                    categoryCode="material_subtypes"
                    valueField="name"
                    value={(field.state.value as string)?.replace(/^(material_subtypes_)/i, "") || ""}
                    onChange={(v) => {
                      const cleanV = v ? v.replace(/^(material_subtypes_)/i, "").trim() : "";
                      field.handleChange(cleanV);
                      form.setFieldValue("typeId", "");
                      handleGenerateInfo({ subType: cleanV, typeId: "" });
                    }}
                    label="Nhóm vật tư *"
                    placeholder="VD: Giấy, Màng..."
                    error={!!field.state.meta.errors.length}
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="typeId">
              {(field: any) => (
                <div className="col-span-2 md:col-span-1">
                  <SharedCombobox
                    type="materialType"
                    value={(field.state.value as string) || ""}
                    onChange={(id) => {
                      field.handleChange(id);
                      handleGenerateInfo({ typeId: id });
                    }}
                    params={currentGroup ? { group: currentGroup } : undefined}
                    label="Loại vật tư"
                    placeholder="VD: Ivory 250gsm"
                    canAdd={true}
                    breadcrumb={[...(props?.breadcrumb || ["Sản phẩm", "Vật tư"]), "Loại vật tư"]}
                    error={!!field.state.meta.errors.length}
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="gsm">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Định lượng (GSM)">
                  <TanstackFormControl>
                    <EditableCombobox
                      type="number"
                      value={String(field.state.value || "")}
                      onChange={(v) => {
                        field.handleChange(v);
                        handleGenerateInfo({ gsm: v });
                      }}
                      suggestions={gsmOptions}
                      placeholder="250"
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="brand">
              {(field: any) => (
                <div className="space-y-1">
                  <ComboboxConfigMaster
                    categoryCode="brands"
                    valueField="name"
                    value={(field.state.value as string)?.replace(/^(brands?_)/i, "") || ""}
                    onChange={(v) => {
                      const cleanV = v ? v.replace(/^(brands?_)/i, "").trim() : "";
                      field.handleChange(cleanV);
                      handleGenerateInfo({ brand: cleanV });
                    }}
                    label="Nhãn hiệu"
                    placeholder="VD: Hansol"
                    error={!!field.state.meta.errors.length}
                  />
                </div>
              )}
            </form.Field>
          </div>

          {/* Hàng 2: Dài x rộng x cao, Nhà cung cấp */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <F
              name="length"
              label="Dài (mm)"
              type="number"
              placeholder="1090"
              onChangeHook={(v) =>
                handleGenerateInfo({ length: Number(v) || undefined })
              }
            />
            <F
              name="width"
              label="Rộng (mm)"
              type="number"
              placeholder="790"
              onChangeHook={(v) =>
                handleGenerateInfo({ width: Number(v) || undefined })
              }
            />
            <F
              name="height"
              label="Cao (mm)"
              type="number"
              placeholder="VD: 10"
              onChangeHook={(v) =>
                handleGenerateInfo({ height: Number(v) || undefined })
              }
            />
            <form.Field name="vendorId">
              {(field: any) => (
                <div className="col-span-2 md:col-span-1">
                  {props?.lockedFields?.includes('vendorId') ? (
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-700">Nhà cung cấp</Label>
                      <div className="p-2 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between h-9">
                        <span className="text-xs font-bold text-slate-800 truncate">
                          {vendorLookup.options.find(v => v.id === field.state.value)?.label || "NCC liên kết"}
                        </span>
                        <span className="text-[10px] text-indigo-700 bg-indigo-100 font-medium px-1.5 py-0.5 rounded shrink-0">
                          Khóa cứng
                        </span>
                      </div>
                    </div>
                  ) : (
                    <SharedCombobox
                      type="vendor"
                      value={(field.state.value as string) || ""}
                      onChange={(id) => field.handleChange(id)}
                      label="Nhà cung cấp"
                      placeholder="Chọn NCC"
                      quickAddLabel="Thêm NCC"
                      canAdd={hasPermission("vendors", "create")}
                      error={!!field.state.meta.errors.length}
                    />
                  )}
                </div>
              )}
            </form.Field>
          </div>

          {/* Hàng 3: ĐVT, Mã vật tư, Tên vật tư, Quy cách khác */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <form.Field name="unit">
              {(field: any) => (
                <div className="col-span-2 md:col-span-1 space-y-1">
                  <ComboboxConfigMaster
                    categoryCode="units"
                    value={(field.state.value as string) || ""}
                    onChange={(v) => field.handleChange(v)}
                    label="Đơn vị tính"
                    placeholder="Tờ, Kg, Mét"
                    error={!!field.state.meta.errors.length}
                  />
                </div>
              )}
            </form.Field>
            <F
              name="specs"
              label="Quy cách khác"
              placeholder="Ghi chú thêm quy cách..."
            />
            <F name="materialId" label="Mã vật tư" placeholder="Tự động" />
            <F
              name="name"
              label="Tên gọi vật tư"
              required
              placeholder="Giấy Ivory 250gsm 790x1090"
              span={2}
            />
          </div>

          {/* Các cột còn lại */}
          <div className="border-t border-slate-100 mt-4 pt-4" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-start">
            <form.Field name="image">
              {() => (
                <div className="space-y-1 col-span-2 md:col-span-1">
                  <Label className="text-xs">Hình ảnh</Label>
                  <div
                    className={cn(
                      "relative border border-dashed rounded-lg cursor-pointer transition-colors h-[72px] w-full max-w-[120px]",
                      "hover:border-indigo-400 hover:bg-indigo-50/50",
                      imagePreview
                        ? "border-indigo-300 bg-indigo-50/30"
                        : "border-slate-200 bg-slate-50",
                      "flex items-center justify-center",
                    )}
                    onPaste={handleImagePaste}
                    tabIndex={0}
                  >
                    {imagePreview ? (
                      <>
                        <ImageLightbox src={imagePreview} alt="Image">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-[68px] max-w-[110px] object-contain rounded"
                          />
                        </ImageLightbox>
                        <button
                          type="button"
                          className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600 transition z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImagePreview("");
                            form.setFieldValue("image", "");
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <div
                        className="text-center p-1 w-full h-full flex flex-col justify-center"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 text-slate-400 mx-auto" />
                        <p className="text-[9px] text-slate-500 mt-0.5">
                          Tải ảnh
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                </div>
              )}
            </form.Field>

            <F name="unitPrice" label="Đơn giá" type="number" placeholder="0" />
            <F
              name="currentStock"
              label="Tồn hiện tại"
              type="number"
              placeholder="0"
            />
            <F
              name="minStock"
              label="Tồn an toàn"
              type="number"
              placeholder="0"
            />

            {/* Trạng thái Ngưng dùng: vật tư chưa có cột trạng thái nên tạm ẩn (02/11/2026) */}
            <div className="col-span-2 md:col-span-4">
              <F
                name="note"
                label="Ghi chú thêm"
                placeholder="Ghi chú nội bộ..."
                span={1}
              />
            </div>
          </div>
        </div>
      </div>
        </form>
  );

  if (isModal) {
    return (
      <div className="space-y-4 p-1">
        {formContent}
        <div className="flex justify-end space-x-2 pt-3 border-t border-slate-200">
          {actions}
        </div>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      title={isEdit ? "Cập nhật vật tư" : "Thêm mới vật tư"}
      breadcrumbs={[
        { label: "Vật tư", to: "/materials" },
        { label: isEdit ? "Cập nhật" : "Thêm mới" },
      ]}
      actions={actions}
    >
      <div className="space-y-4 max-w-5xl mx-auto py-2">
        {formContent}
        <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
          {actions}
        </div>
      </div>
    </StandardDetailLayout>
  );
}

export default MaterialForm;
